import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  setPersistence,
  browserLocalPersistence,
} from "firebase/auth";
import { auth } from "./firebase";
import { Alert, AlertIcon, AlertTitle } from "./components/ui/alert";
import { RiCheckboxCircleFill, RiEyeLine, RiEyeOffLine } from "@remixicon/react";
import { BASE_URL } from "./apiConfig";
import { validateEmail } from "./lib/guardrails";
import { tenantHeaders, getTenantSlug } from "./lib/tenantConfig";
import ucdpLogo from "./assets/logo3.png";
import bgImage from "./assets/bg-4.png";

const Login: React.FC = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [showErrorAlert, setShowErrorAlert] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  // Shared post-auth flow — called after both email/password and Google sign-in
  const postAuthFlow = async (token: string, uid: string, email: string) => {
    localStorage.setItem("authToken", token);
    localStorage.setItem("uid", uid);
    localStorage.setItem("userEmail", email);
    const tenantSlug = getTenantSlug();
    if (tenantSlug) localStorage.setItem("tenantSlug", tenantSlug);
    else localStorage.removeItem("tenantSlug");

    const response = await fetch(`${BASE_URL}/user/verify`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: token, ...tenantHeaders() },
    });
    const data = await response.json();
    if (response.ok && data.status === "success") {
      localStorage.setItem("userRole", data.role);
      setShowSuccessAlert(true);
      const emailDomain = email.split("@")[1]?.toLowerCase() ?? "";
      const publicDomains = new Set(["gmail.com","yahoo.com","hotmail.com","outlook.com","live.com","icloud.com","mail.com","protonmail.com","aol.com","rediffmail.com","zoho.com"]);
      const slug = data.is_new && !publicDomains.has(emailDomain) ? emailDomain.split(".")[0] : null;
      setTimeout(() => {
        setShowSuccessAlert(false);
        if (slug) window.location.href = `https://${slug}.usecases.tech/`;
        else navigate("/");
      }, 2000);
    } else {
      const msg = data.detail === "email_not_verified"
        ? "Please verify your email before logging in."
        : (data.message || "Login failed");
      setErrorMsg(msg);
      setShowErrorAlert(true);
      setTimeout(() => setShowErrorAlert(false), 3000);
    }
  };

  const handleGoogleSignIn = async () => {
    setLoading(true);
    try {
      await setPersistence(auth, browserLocalPersistence);
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      const token = await result.user.getIdToken();
      await postAuthFlow(token, result.user.uid, result.user.email || "");
    } catch (error: any) {
      if (error.code === "auth/popup-closed-by-user") return; // user dismissed popup
      setErrorMsg("Google sign-in failed. Please try again.");
      setShowErrorAlert(true);
      setTimeout(() => setShowErrorAlert(false), 3000);
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    if (!email.trim() || !password.trim()) {
      setErrorMsg("Email and password are required.");
      setShowErrorAlert(true);
      setTimeout(() => setShowErrorAlert(false), 3000);
      return false;
    }
    const emailCheck = validateEmail(email);
    if (!emailCheck.valid) {
      setErrorMsg(emailCheck.error!);
      setShowErrorAlert(true);
      setTimeout(() => setShowErrorAlert(false), 3000);
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);
    try {
      await setPersistence(auth, browserLocalPersistence);
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const token = await userCredential.user.getIdToken();
      await postAuthFlow(token, userCredential.user.uid, userCredential.user.email || "");
    } catch (error: any) {
      console.error("Login error:", error);
      let msg = "Login error";
      if (error.code === "auth/invalid-credential" || error.code === "auth/user-not-found") {
        msg = "Invalid email or password.";
      } else if (error.code === "auth/wrong-password") {
        msg = "Incorrect password.";
      }
      setErrorMsg(msg);
      setShowErrorAlert(true);
      setTimeout(() => setShowErrorAlert(false), 3000);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="h-screen flex overflow-hidden bg-[#0d0d0d]">

      {/* ── LEFT PANEL — Image + Branding (desktop only) ── */}
      <div className="hidden md:flex relative w-[60%] h-full flex-shrink-0 flex-col">
        {/* Background image — shifted right so subject face stays visible */}
        <img
          src={bgImage}
          alt="UCDP Discovery Platform"
          className="absolute inset-0 w-full h-full object-cover"
          style={{ objectPosition: "70% center" }}
          draggable={false}
        />
        {/* Dark gradient overlay — stronger at bottom for text readability */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(to bottom, rgba(0,0,0,0.25) 0%, rgba(0,0,0,0.10) 35%, rgba(0,0,0,0.82) 70%, rgba(0,0,0,0.95) 100%)",
          }}
        />
        {/* Orange tint */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(160deg, rgba(255,84,31,0.28) 0%, transparent 55%)",
          }}
        />

        {/* Logo — top left */}
        <div className="relative z-10 p-8">
          <Link to="/">
            <img src={ucdpLogo} alt="UCDP" className="h-24 w-auto drop-shadow-lg" />
          </Link>
        </div>

        {/* Bottom content — headline + bullets */}
        <div className="relative z-10 mt-auto px-10 pb-14">
          <h2 className="font-ibm-plex-sans font-bold text-[42px] leading-[112%] tracking-[-0.03em] text-white mb-5">
            Discover AI Use Cases<br />Across Every Sector
          </h2>
          <p className="font-lora font-normal text-[13px] leading-[22px] text-[#ffffffa0] mb-10 max-w-[400px]">
            Instantly search, compare, and explore real-world AI applications transforming industries worldwide.
          </p>

          {/* Feature bullets */}
          <ul className="flex flex-col gap-[14px]">
            {[
              "Search Use Cases by Sector & Sub-Sector",
              "Discover Startups, VCs & Acquisition Targets",
              "Agentic AI for Sector-Specific Insights",
            ].map((item) => (
              <li key={item} className="flex items-center gap-3">
                <span
                  className="w-2 h-2 rounded-full flex-shrink-0 ring-2 ring-[#FF541F]/30"
                  style={{ background: "#FF541F" }}
                />
                <span className="font-inter text-[13px] font-medium text-[#ffffffcc]">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* ── RIGHT PANEL — Login Form ── */}
      <div className="relative flex flex-col w-full md:w-[40%] h-full overflow-y-auto"
        style={{ background: "#0e1016", borderLeft: "1px solid rgba(255,84,31,0.25)" }}>

        {/* Subtle top glow */}
        <div
          className="absolute top-0 left-0 right-0 h-[300px] pointer-events-none"
          style={{
            background:
              "radial-gradient(70% 70% at 50% 0%, rgba(255,84,31,0.12) 0%, transparent 100%)",
          }}
        />

        {/* Mobile logo */}
        <nav className="relative z-10 p-5 md:hidden">
          <Link to="/">
            <img src={ucdpLogo} alt="UCDP" className="h-24 w-auto drop-shadow-lg" />
          </Link>
        </nav>

        {/* Centered form */}
        <div className="relative z-10 flex flex-1 items-center justify-center px-8 py-10">
          <div className="w-full max-w-[340px] flex flex-col gap-6">

            {/* Heading */}
            <div className="flex flex-col gap-[6px]">
              <p className="text-[#FF541F] font-inter font-semibold text-[15px] uppercase tracking-[0.15em] mb-1">
                AI-Powered Discovery Platform
              </p>
              <h3 className="font-inter font-bold text-[28px] leading-[36px] text-white">
                Welcome back
              </h3>
              <p className="font-inter font-normal text-[14px] text-[#4b5563]">
                Sign in to your UCDP account
              </p>
            </div>

            {/* Google Sign-In */}
            <button
              type="button"
              onClick={handleGoogleSignIn}
              disabled={loading}
              className="w-full h-[46px] flex items-center justify-center gap-3 rounded-[12px] font-inter font-medium text-[14px] text-white transition-all hover:bg-[#1e2330] active:scale-[0.98] disabled:opacity-60"
              style={{ background: "#161a23", border: "1px solid #1f2433" }}
            >
              <svg width="18" height="18" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M43.611 20.083H42V20H24V28H35.303C33.654 32.657 29.223 36 24 36C17.373 36 12 30.627 12 24C12 17.373 17.373 12 24 12C27.059 12 29.842 13.154 31.961 15.039L37.618 9.382C34.046 6.053 29.268 4 24 4C12.955 4 4 12.955 4 24C4 35.045 12.955 44 24 44C35.045 44 44 35.045 44 24C44 22.659 43.862 21.35 43.611 20.083Z" fill="#FFC107"/>
                <path d="M6.306 14.691L12.877 19.51C14.655 15.108 18.961 12 24 12C27.059 12 29.842 13.154 31.961 15.039L37.618 9.382C34.046 6.053 29.268 4 24 4C16.318 4 9.656 8.337 6.306 14.691Z" fill="#FF3D00"/>
                <path d="M24 44C29.166 44 33.86 42.023 37.409 38.808L31.219 33.57C29.211 35.091 26.715 36 24 36C18.798 36 14.381 32.683 12.717 28.054L6.195 33.079C9.505 39.556 16.227 44 24 44Z" fill="#4CAF50"/>
                <path d="M43.611 20.083H42V20H24V28H35.303C34.511 30.237 33.072 32.166 31.217 33.571L31.219 33.57L37.409 38.808C36.971 39.205 44 34 44 24C44 22.659 43.862 21.35 43.611 20.083Z" fill="#1976D2"/>
              </svg>
              Continue with Google
            </button>

            {/* Divider */}
            <div className="flex items-center gap-3">
              <div className="flex-1 h-px" style={{ background: "#1f2433" }} />
              <span className="font-inter text-[12px] text-[#374151]">or sign in with email</span>
              <div className="flex-1 h-px" style={{ background: "#1f2433" }} />
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="flex flex-col gap-4">

              {/* Email */}
              <div className="flex flex-col gap-[6px]">
                <label className="font-inter text-[13px] font-medium text-[#9ca3af]" htmlFor="email">
                  Email address
                </label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full h-[46px] px-4 rounded-[12px] text-white text-[14px] placeholder-[#374151] focus:outline-none focus:ring-1 focus:ring-[#FF541F] transition-all"
                  style={{ background: "#161a23", border: "1px solid #1f2433" }}
                  placeholder="you@example.com"
                />
              </div>

              {/* Password */}
              <div className="flex flex-col gap-[6px]">
                <div className="flex items-center justify-between">
                  <label className="font-inter text-[13px] font-medium text-[#9ca3af]" htmlFor="password">
                    Password
                  </label>
                  <Link
                    to="/forgot-password"
                    className="font-inter text-[12px] !text-[#FF541F] hover:!text-[#ff6a38] transition-colors no-underline"
                  >
                    Forgot password?
                  </Link>
                </div>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full h-[46px] px-4 pr-11 rounded-[12px] text-white text-[14px] placeholder-[#374151] focus:outline-none focus:ring-1 focus:ring-[#FF541F] transition-all"
                    style={{ background: "#161a23", border: "1px solid #1f2433" }}
                    placeholder="••••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#4b5563] hover:text-[#9ca3af] focus:outline-none"
                  >
                    {showPassword ? <RiEyeOffLine className="w-5 h-5" /> : <RiEyeLine className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {/* Remember me */}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="remember"
                  className="w-4 h-4 rounded accent-[#FF541F]"
                />
                <label htmlFor="remember" className="font-inter text-[13px] text-[#6b7280]">
                  Remember me for 30 days
                </label>
              </div>

              {/* Sign In button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full h-[48px] mt-2 rounded-[12px] font-inter font-semibold text-[15px] text-white transition-all hover:scale-[1.02] hover:shadow-2xl active:scale-[0.98] disabled:opacity-60"
                style={{
                  background: "linear-gradient(180deg, #ff6535 0%, #e8430d 100%)",
                  boxShadow: "0 6px 28px rgba(255,84,31,0.45)",
                }}
              >
                {loading ? "Signing in…" : "Sign In"}
              </button>

              <p className="text-center font-inter text-[13px] text-[#6b7280] mt-1">
                Don't have an account?{" "}
                <Link to="/signup" className="!text-[#FF541F] hover:!text-[#ff6a38] font-medium no-underline">
                  Create account
                </Link>
              </p>
            </form>
          </div>
        </div>
      </div>

      {/* Success Alert */}
      {showSuccessAlert && (
        <div className="fixed top-3 left-1/2 z-50 -translate-x-1/2 w-full max-w-md">
          <Alert variant="success" close={true} onClose={() => setShowSuccessAlert(false)}>
            <AlertIcon>
              <RiCheckboxCircleFill />
            </AlertIcon>
            <AlertTitle>Logged in successfully!</AlertTitle>
          </Alert>
        </div>
      )}

      {/* Error Alert */}
      {showErrorAlert && (
        <div className="fixed top-3 left-1/2 z-50 -translate-x-1/2 w-full max-w-md">
          <Alert variant="destructive" close={true} onClose={() => setShowErrorAlert(false)}>
            <AlertIcon><span className="text-xl">!</span></AlertIcon>
            <AlertTitle>{errorMsg || "Login failed. Please try again."}</AlertTitle>
          </Alert>
        </div>
      )}
    </div>
  );
};

export default Login;